import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useStorage } from '../hooks/useStorage';
import {
  getLogo,
  getLogoType,
  getPoweredByLogo,
} from '../utils/localizedAsset';
import localeMapKeyVals from '../utils/localeMap.json';

const LANG_STORAGE_KEY = 'selected-language';

const getInitLanguage = () => {
  // For web, we'll use the browser's language
  const browserLang = navigator.language || 'en-US';
  return browserLang;
};

interface LanguageContextType {
  selectedLanguage: string;
  setSelectedLanguage: (lang: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
  Logo: React.ComponentType<any>;
  LogoType: React.ComponentType<any>;
  PoweredByLogo: React.ComponentType<any>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { t: translate } = useTranslation();
  const { getItem, setItem } = useStorage();
  const [selectedLanguage, setSelectedLanguage] = useState(
    getItem(LANG_STORAGE_KEY) || getInitLanguage(),
  );

  useEffect(() => {
    if (selectedLanguage) {
      setItem(LANG_STORAGE_KEY, selectedLanguage);
      // @ts-ignore
      const locale =
        (localeMapKeyVals as Record<string, string>)[selectedLanguage] ||
        selectedLanguage;
      i18next.changeLanguage(locale);
    }
  }, [selectedLanguage, setItem]);

  const Logo = useMemo(() => getLogo(selectedLanguage), [selectedLanguage]);
  const LogoType = useMemo(
    () => getLogoType(selectedLanguage),
    [selectedLanguage],
  );
  const PoweredByLogo = useMemo(
    () => getPoweredByLogo(selectedLanguage),
    [selectedLanguage],
  );

  const value = useMemo(
    () => ({
      selectedLanguage,
      setSelectedLanguage,
      t: translate,
      Logo,
      LogoType,
      PoweredByLogo,
    }),
    [selectedLanguage, translate, Logo, LogoType, PoweredByLogo],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
